import type { DerivedYourPlanCategory } from '../../deriveSipPlansView'
import { SipCategoryBucketKey, type SipActionFilter } from '../../../api/types'
import { CategoryCard } from '../../../../lumpsum/plans-list/lumpsum-ui-components/CategoryCard'
import { PlansCategoryRangeTrailing } from '../../../../lumpsum/plans-list/lumpsum-ui-components/PlansCategoryRangeTrailing'
import { SipCategoryErrorState } from '../../sip-ui-components/SipCategoryErrorState'
import { StopSipBucketAccordion } from './buckets/StopSipBucketAccordion'
import { StartSipBucketAccordion } from './buckets/StartSipBucketAccordion'
import { IncreaseSipBucketAccordion } from './buckets/IncreaseSipBucketAccordion'
import { DecreaseSipBucketAccordion } from './buckets/DecreaseSipBucketAccordion'
import { NoActionSipBucketAccordion } from './buckets/NoActionSipBucketAccordion'
import type { ReactNode } from 'react'
import { buildPlanAllocationRangeLabel } from '../../../../display-values/lumpsumPlanPercentage'
import { getVisibleSipCategoryBucketKeys } from '../../../../utils/sip/planListUtils'
import { EventNames } from '../../../../../../../types/misc.types'
import { lightImpactVibrate, postCTA, trackEvent } from '../../../../../../../utils/appBridge'
import { PlansScreen, PlansSipListEventIds } from '../../../../analytics/plansEvents'
import { useSipPlansContext } from '../../SipPlansProvider'

type YourPlanCategoryCardProps = {
  category: DerivedYourPlanCategory
  categoryIndex: number
  actionFilter: SipActionFilter
  noDivider?: boolean
}

type YourPlanBucketAccordionProps = {
  category: DerivedYourPlanCategory
  categoryId: string
  actionFilter: SipActionFilter
  isBorderTop?: boolean
}

export function YourPlanCategoryCard({
  category,
  categoryIndex,
  actionFilter,
  noDivider = false
}: YourPlanCategoryCardProps) {
  const categoryId = category?.categoryId ?? `category-${categoryIndex}`
  const label = category?.categoryName ?? ''
  const categoryAllocationRangeLabel = buildPlanAllocationRangeLabel({
    idealAllocation: category?.idealAllocationPercentage
  })
  const sharedBucketProps = {
    category,
    categoryId,
    actionFilter
  }

  return (
    <CategoryCard
      noDivider={noDivider}
      header={
        <YourPlanCategoryHeader
          category={category}
          label={label}
          categoryAllocationRangeLabel={categoryAllocationRangeLabel}
        />
      }
    >
      {category.hasCategoryError ? (
        <SipCategoryErrorState />
      ) : (
        getVisibleSipCategoryBucketKeys(category, actionFilter).map((bucketKey, bucketIndex) =>
          renderYourPlanCategoryBucket(bucketKey, {
            ...sharedBucketProps,
            isBorderTop: bucketIndex > 0
          })
        )
      )}
    </CategoryCard>
  )
}

function YourPlanCategoryHeader({
  category,
  label,
  categoryAllocationRangeLabel
}: {
  category: DerivedYourPlanCategory
  label: string
  categoryAllocationRangeLabel: string
}) {
  const { plansOverview } = useSipPlansContext()

  function handleViewStrategyClick() {
    const slotBottomsheetCta = plansOverview?.slotBottomsheetCta
    if (slotBottomsheetCta == null || typeof slotBottomsheetCta !== 'object') return

    const group = category?.group ?? ''
    const categoryKey = category?.categoryId ?? ''
    const ctaRecord = { ...(slotBottomsheetCta as Record<string, unknown>) }
    if (typeof ctaRecord.url === 'string') {
      ctaRecord.url = ctaRecord.url
        .replace(/(group=)[^&]*/, `$1${encodeURIComponent(group)}`)
        .replace(/(category=)[^&]*/, `$1${encodeURIComponent(categoryKey)}`)
    }

    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: PlansScreen.SipYourPlan,
        eventId: PlansSipListEventIds.ViewStrategy,
        buttonLabel: 'View strategy'
      }
    })
    postCTA(JSON.stringify(ctaRecord))
  }

  return (
    <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-4">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="text-[14px] font-[500] leading-[140%] text-[#232323]">{label}</div>
        <button
          type="button"
          className="w-fit p-0 text-left text-[13px] font-[500] leading-[140%] text-[#1470CC]"
          onClick={handleViewStrategyClick}
        >
          View strategy
        </button>
      </div>
      <PlansCategoryRangeTrailing label={categoryAllocationRangeLabel} />
    </div>
  )
}

function renderYourPlanCategoryBucket(
  bucketKey: SipCategoryBucketKey,
  bucketProps: YourPlanBucketAccordionProps
): ReactNode {
  if (bucketKey === SipCategoryBucketKey.stopRecommendations) {
    return <StopSipBucketAccordion key={bucketKey} {...bucketProps} />
  }
  if (bucketKey === SipCategoryBucketKey.startRecommendations) {
    return <StartSipBucketAccordion key={bucketKey} {...bucketProps} />
  }
  if (bucketKey === SipCategoryBucketKey.increaseRecommendations) {
    return <IncreaseSipBucketAccordion key={bucketKey} {...bucketProps} />
  }
  if (bucketKey === SipCategoryBucketKey.decreaseRecommendations) {
    return <DecreaseSipBucketAccordion key={bucketKey} {...bucketProps} />
  }
  if (bucketKey === SipCategoryBucketKey.noActionFunds) {
    return <NoActionSipBucketAccordion key={bucketKey} {...bucketProps} />
  }
  return null
}
